import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Search } from "lucide-react";

const FAQS = [
  {
    cat: "Sessions",
    q: "What is Past Life Regression Therapy?",
    a: `Past Life Regression Therapy is a gentle, guided hypnotic process that allows you to access memories or impressions believed to originate from other lifetimes. In this deeply relaxed, altered state of consciousness, you can explore the underlying causes of recurring patterns, emotions, or relationships.\n\nWhether these experiences are understood as literal past lives or symbolic expressions of the subconscious, the insights gained often lead to emotional release, clarity, and a deeper understanding of self.`,
  },
  {
    cat: "Sessions",
    q: "What is Soul Regression Therapy?",
    a: `Soul Regression Therapy (also known as Between Lives or Spiritual Regression) guides you beyond individual lifetimes into the space between incarnations — a realm of expanded awareness.\n\nIn this state, you may reconnect with your higher self, spirit guides, and soul family. Many clients experience profound healing, life review, and a deeper sense of their soul's journey, purpose, and lessons across lifetimes.`,
  },
  {
    cat: "The Science",
    q: "How does hypnosis work — and what happens in the brain?",
    a: `Hypnosis is a natural, scientifically recognised state of focused relaxation and heightened awareness. During this process, brainwave activity shifts:\n\n• Beta (13–30 Hz): active thinking and everyday awareness\n• Alpha (8–13 Hz): relaxed, calm focus — the gateway to the subconscious\n• Theta (4–8 Hz): deep relaxation and meditative states — ideal for regression, where imagery, memory, and intuitive insight emerge\n• Delta (0.5–4 Hz): deep sleep and unconscious states\n\nDuring regression, the mind gently transitions from beta into alpha and theta states, allowing access to deeper layers of awareness while you remain present, conscious, and in control.`,
  },
  {
    cat: "The Science",
    q: "What are the subconscious and superconscious minds?",
    a: `The subconscious mind stores memories, emotions, habits, and experiences — including those beyond conscious recall. It communicates through imagery, sensation, and intuition.\n\nThe superconscious mind (often referred to as the higher self or soul consciousness) represents the timeless, non-physical aspect of you — connected to deeper wisdom, guidance, and universal intelligence.\n\nRegression therapy bridges these layers, allowing access to subconscious material while opening communication with the superconscious for insight, healing, and alignment.`,
  },
  {
    cat: "The Science",
    q: "Where did regression therapy originate?",
    a: `Modern regression therapy has been shaped by pioneers who bridged psychology, hypnotherapy, and spirituality:\n\n• Dr Brian Weiss — documented spontaneous past-life recall in clinical settings (Many Lives, Many Masters)\n• Dolores Cannon — developed QHHT (Quantum Healing Hypnosis Technique), exploring higher-self healing and soul evolution\n• Dr Michael Newton — pioneered Life Between Lives regression (Journey of Souls)\n• Dick Sutphen — helped bring hypnotic regression into mainstream awareness\n\nTheir work has inspired a growing movement of individuals reconnecting with consciousness, healing, and the deeper nature of the soul.`,
  },
  {
    cat: "Beliefs",
    q: "Is regression therapy in conflict with religion or belief systems?",
    a: `No. Regression therapy is not a religion and does not require any particular belief system.\n\nIt is an experiential process that can be interpreted through your own worldview. Clients from many faiths and secular backgrounds often find that the experience deepens compassion, connection, and personal spirituality rather than conflicting with it.`,
  },
  {
    cat: "The Experience",
    q: "What does a session feel like?",
    a: `Most clients describe the experience as deeply relaxing — similar to meditation.\n\nYou may see imagery, smell scents, hear sounds, feel emotions, or receive intuitive impressions. Some experience vivid scenes, while others sense knowing or subtle guidance. Each session unfolds uniquely, in the way that is most natural for you.`,
  },
  {
    cat: "The Experience",
    q: "How long is a session?",
    a: `• Past Life Regression: approximately 1.5 – 2 hours\n• Soul Regression: approximately 2.5 – 3 hours\n\nThis includes pre-session intake, hypnosis induction and regression experience, and post-session integration.`,
  },
  {
    cat: "The Experience",
    q: "Can anyone be hypnotised?",
    a: `Yes — almost everyone can enter a hypnotic state with willingness and guidance.\n\nHypnosis is not sleep or loss of control; it is a cooperative state of focused awareness. You remain conscious, aware, and able to communicate at all times.`,
  },
  {
    cat: "Safety",
    q: "Is it safe?",
    a: `Yes. Hypnosis is a natural mental state that you experience daily — such as during daydreaming or becoming absorbed in thought.\n\nYou always remain in control throughout. Sessions are conducted in a safe, supportive, and confidential environment.`,
  },
  {
    cat: "Safety",
    q: "What if I experience strong emotions?",
    a: `Emotional responses are a natural part of the healing process.\n\nThey are approached gently and with care, allowing for safe release and integration. Most clients leave feeling lighter, clearer, and more at peace.`,
  },
  {
    cat: "Beliefs",
    q: "Do I need to believe in reincarnation for this to work?",
    a: `No. The process is effective regardless of your beliefs.\n\nExperiences may be understood as symbolic expressions of the subconscious or as genuine soul memories. Either way, the insights received are meaningful and relevant to your personal growth and healing.`,
  },
  {
    cat: "Practical",
    q: "Is an online session as effective as an in-person session?",
    a: `Yes. Regression therapy works through the subconscious and superconscious mind — both of which are not limited by physical location.\n\nOnline sessions provide the same depth of relaxation, connection, and outcome as in-person sessions. They are conducted via Doxy.me, a secure, encrypted telehealth platform that ensures privacy and ease of use.`,
  },
  {
    cat: "Practical",
    q: "How should I prepare for my session?",
    a: `To help you get the most from your session, I recommend the following:

• Choose a quiet, private space where you won't be disturbed for the duration of the session, with a stable internet connection.
• Wear comfortable clothing and make sure you are physically at ease before we begin.
• Have a glass or bottle of water and tissues within reach.
• Avoid alcohol, caffeine, or recreational substances for at least 24 hours prior.
• Set your phone to silent and turn off any notifications.
• Allow yourself some time before and after the session so you don't feel rushed.
• Reflect beforehand on any questions, intentions, or areas of your life — purpose, relationships, health, recurring patterns — you would like to explore (5–10 themes work well).
• For the best experience, I strongly recommend using headphones or earbuds. This minimises distractions, deepens relaxation, and lets you hear my guidance clearly throughout the session.

Come with an open, relaxed mindset and without expectations of what you "should" experience. Every journey unfolds uniquely — trust your subconscious to bring forward whatever is most beneficial for your highest good.`,
  },
  {
    cat: "Practical",
    q: "Will I remember what happens?",
    a: `Yes. You remain aware throughout the session and can recall your experience similarly to a vivid dream or meditation.\n\nWith your permission, sessions can be recorded so you can revisit insights, as understanding often continues to deepen for days and weeks afterward.`,
  },
  {
    cat: "Safety",
    q: "Is regression therapy a substitute for medical or psychological treatment?",
    a: `No. Regression therapy is a complementary approach and does not replace medical or psychological care.\n\nIt supports emotional, energetic, and spiritual wellbeing alongside conventional treatment.`,
  },
  {
    cat: "Practical",
    q: "Who facilitates the sessions?",
    a: `All sessions are facilitated by Kassie Thomas-Smith, a certified Past Life and Soul Regression Therapist.\n\nHer training includes the Hypnotherapy Learning Hub (Lorna & John Jackson), with mentorship under Dr Jeannet Weijers. Her work is also informed by pioneers such as Brian Weiss, Dolores Cannon, and Michael Newton.\n\nShe integrates professional hypnotherapy with compassion, grounded awareness, and a deep respect for the soul's journey.`,
  },
  {
    cat: "Practical",
    q: "How do I book a session?",
    a: `Sessions are available securely online via Doxy.me.\n\nYou can book directly through the "Sessions" page and receive all preparation details.\n\nPayment is handled securely online. Alternative payment options are available upon request.`,
  },
];

const CATEGORIES = ["All", "Sessions", "The Experience", "The Science", "Beliefs", "Safety", "Practical"];

export default function FaqSection() {
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) =>
      (active === "All" || f.cat === active) &&
      (!q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))
    );
  }, [active, query]);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 px-6 md:px-12 border-t border-white/5 overflow-hidden"
    >
      {/* Soft cosmic glow behind */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.07),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <p className="font-sans text-xs tracking-overline text-[#D4AF37] mb-4">04 — frequently asked</p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white tracking-tight">
            Questions, <span className="italic gold-text">answered.</span>
          </h2>
          <p className="mt-6 font-sans text-base sm:text-lg text-slate-300 leading-relaxed">
            Everything you may want to know about hypnotic regression, the science behind the process,
            and what to expect from your private session.
          </p>
        </motion.div>

        {/* Search + categories */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sticky sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start">
            <div className="glass-card rounded-2xl p-6">
              <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37] mb-3">search</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]/70" />
                <input
                  data-testid="faq-search-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Type a keyword..."
                  className="cosmic-input rounded-xl pl-10 pr-4 py-3 w-full font-sans text-sm"
                />
              </div>

              <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37] mt-7 mb-3">categories</p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    data-testid={`faq-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                    type="button"
                    onClick={() => setActive(c)}
                    className={`px-3.5 py-2 rounded-full text-xs font-sans border transition-all ${
                      active === c
                        ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                        : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#D4AF37]/40 hover:text-white"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="mt-7 pt-6 border-t border-white/5">
                <p className="font-serif italic text-lg text-white leading-snug">
                  "Still curious?"
                </p>
                <p className="mt-2 font-sans text-sm text-slate-400 leading-relaxed">
                  Book an Intuitive Insight session — 45 minutes of focused guidance for whatever you're navigating.
                </p>
                <a
                  href="#sessions"
                  data-testid="faq-cta-sessions"
                  className="mt-4 btn-ghost-gold rounded-full px-5 py-2.5 text-xs inline-flex items-center"
                >
                  Explore sessions
                </a>
              </div>
            </div>
          </aside>

          {/* Accordion */}
          <div className="lg:col-span-8">
            {filtered.length === 0 ? (
              <div className="glass-card rounded-2xl p-10 text-center" data-testid="faq-empty">
                <p className="font-serif text-2xl text-white">Nothing matches "{query}"</p>
                <p className="mt-2 text-slate-400 font-sans text-sm">Try another keyword or clear the filters.</p>
              </div>
            ) : (
              <Accordion
                type="single"
                collapsible
                className="space-y-4"
                data-testid="faq-accordion"
              >
                {filtered.map((f, i) => (
                  <motion.div
                    key={`${f.cat}-${i}-${f.q}`}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.45, delay: Math.min(i * 0.04, 0.3) }}
                  >
                    <AccordionItem
                      value={`item-${i}`}
                      data-testid={`faq-item-${i}`}
                      className="glass-card rounded-2xl border-0 px-6 data-[state=open]:border-[#D4AF37]/30"
                    >
                      <AccordionTrigger className="py-5 hover:no-underline group">
                        <div className="flex items-start gap-5 text-left flex-1 min-w-0">
                          <span className="font-serif text-2xl text-[#D4AF37]/70 group-data-[state=open]:text-[#D4AF37] shrink-0 leading-none mt-1">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37]/70 mb-1">
                              {f.cat}
                            </p>
                            <p className="font-serif text-lg sm:text-xl text-white leading-snug">
                              {f.q}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-6 pl-12 pr-2">
                        <p className="font-sans text-slate-300 leading-relaxed whitespace-pre-line">
                          {f.a}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
